import { jsx } from 'react/jsx-runtime';
import { useRef, useMemo, useCallback } from 'react';
import selectableContainerStyles from './SelectableContainer.styles.esm.js';
import { SelectableContent } from './SelectableContent.esm.js';
import { SelectableLink } from './SelectableLink.esm.js';
import { isSlotType } from './utils.esm.js';

function useSelectableContainer(props) {
    const { variant, label, secondaryLabel, linkProps, disabled = false, children, showInput = true, showSecondaryLabel = true, showLink = true, inverse = false, } = props;
    const linkRef = useRef(null);
    const styles = useMemo(() => selectableContainerStyles({ disabled, inverse }), [disabled, inverse]);
    const { base, inputColumn, contentColumn, actionColumn, label: labelClass, secondaryLabel: secondaryLabelClass, labelWrapper, slotWrapper, } = styles;
    const handleKeyDown = useCallback((e) => {
        if (disabled)
            return;
        if (e.key === "Enter" && linkRef.current) {
            e.preventDefault();
            linkRef.current.click();
        }
    }, [disabled]);
    const ariaLabel = useMemo(() => {
        if (children && isSlotType(variant))
            return undefined;
        return `${label || ""}${showSecondaryLabel && secondaryLabel ? `, ${secondaryLabel}` : ""}`;
    }, [children, variant, label, showSecondaryLabel, secondaryLabel]);
    const mergedLinkProps = useMemo(() => {
        const defaultLinkProps = {
            size: "sm",
            variant: "standalone",
        };
        if (!linkProps) {
            if (!showLink)
                return undefined;
            return undefined;
        }
        return Object.assign(Object.assign({}, defaultLinkProps), linkProps);
    }, [linkProps, showLink]);
    const renderContentColumn = useCallback(() => (jsx(SelectableContent, { variant: variant, label: label, secondaryLabel: showSecondaryLabel ? secondaryLabel : undefined, children: children, contentColumn: contentColumn, labelClass: labelClass, secondaryLabelClass: secondaryLabelClass, labelWrapper: labelWrapper, slotWrapper: slotWrapper })), [
        variant,
        label,
        secondaryLabel,
        showSecondaryLabel,
        children,
        contentColumn,
        labelClass,
        secondaryLabelClass,
        slotWrapper,
    ]);
    const renderLinks = useCallback(() => {
        if (!showLink)
            return null;
        return (jsx(SelectableLink, { variant: variant, ref: linkRef, linkProps: mergedLinkProps, inverse: inverse, disabled: disabled, actionColumn: actionColumn }));
    }, [showLink, variant, mergedLinkProps, inverse, disabled, actionColumn]);
    return {
        base,
        inputColumn,
        ariaLabel,
        handleKeyDown,
        renderContentColumn,
        renderLinks,
        showInput,
    };
}

export { useSelectableContainer };
